import { Controller, useForm } from "react-hook-form";
import { useBookingFlow } from "../../context/use-booking-flow";
import { Button } from "@/components/ui/button";
import { yupResolver } from "@hookform/resolvers/yup";
import { UpsellSchema } from "./booking-upsell.validation";
import { BookingUpsellFormData } from "./booking-upsell.types";
import { useEffect, useState } from "react";
import { SirenIcon } from "lucide-react";
import { isProRataDate } from "@/utils/is-pro-rata-date";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { GetProRataCredits } from "../../adapters/get-pro-rata-credits/get-pro-rata-credits";
import { AdditionalPaymentsTable } from "../additional-payments-table";
import { UpsellCyclesTable } from "../updated-cycles-table";
import { BookingFlowHeader } from "../booking-flow-header";
import { JustDecimal } from "@/utils/format/regex/just-decimal";
import { Input } from "@/components/inputs/input";

export const BookingUpsell = () => {
  const [errorMsg, setErrorMsg] = useState("");
  const [upsellOption, setUpsellOption] = useState(0);
  const [upsellProRataOption, setUpsellProRataOption] = useState(0);
  const {
    setUpsellData,
    previousStep,
    nextStep,
    upsellData,
    bookingData,
    SimulateUpsell,
    simulatedUpsell,
    UpdateUpsellPayments,
    UpdateUpsellCycles,
  } = useBookingFlow();

  const hasProRata =
    !!bookingData?.start_date && isProRataDate(bookingData.start_date);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<BookingUpsellFormData>({
    resolver: yupResolver(UpsellSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      upsellOption: upsellData?.upsellOption,
      upsellProRataOption: upsellData?.upsellProRataOption,
    },
  });

  const onSubmit = async (data: BookingUpsellFormData) => {
    try {
      setErrorMsg("");
      setUpsellData({ ...data, simulatedUpsellData: simulatedUpsell });
      nextStep();
    } catch (error) {
      setErrorMsg(
        error instanceof Error ? error.message : "Erro ao processar upsell",
      );
    }
  };

  const handleProRataOptionChange = (credits: number) => {
    if (simulatedUpsell && credits) {
      const { newPayments, updatedCycles } = simulatedUpsell;

      if (newPayments.length) {
        const updatedPayments = newPayments.map((payment) => {
          if (payment.proRata) {
            const creditUnitValue = payment.amount / payment.capacity;
            return {
              ...payment,
              amount: creditUnitValue * credits,
              capacity: credits,
            };
          }

          return payment;
        });

        setUpsellProRataOption(credits);
        UpdateUpsellPayments(updatedPayments);
      }

      const updatedCyclesData = updatedCycles.map((cycle) => {
        if (cycle.proRata) {
          const creditUnitValue = cycle.oldAmount / cycle.oldCapacity;
          return {
            ...cycle,
            newAmount: cycle.oldAmount + creditUnitValue * credits,
            newCapacity: credits + cycle.oldCapacity,
          };
        }

        return cycle;
      });

      UpdateUpsellCycles(updatedCyclesData);
    }
  };

  const calculateProRataCredits = async (
    startDate: string,
    credits: number,
  ) => {
    const proRataCredits = await GetProRataCredits(
      startDate,
      credits,
      bookingData!.company.id,
    );

    setUpsellProRataOption(proRataCredits);
    setValue("upsellProRataOption", proRataCredits);
  };

  useEffect(() => {
    if (upsellOption && bookingData) {
      SimulateUpsell({
        creditAmount: upsellOption,
        beginDate: bookingData.start_date,
        companyId: bookingData.company.id,
        packageId: bookingData.package.id,
      });

      hasProRata &&
        calculateProRataCredits(bookingData.start_date, upsellOption);
    }

    if (!upsellOption && upsellData?.upsellOption) {
      setUpsellOption(upsellData.upsellOption);
    }
  }, [upsellOption]);

  return (
    <div className="bg-slate-50">
      <div className="p-6 max-w-4xl mx-auto bg-slate-50 h-full gap-8 flex flex-col">
        <BookingFlowHeader
          onClick={() => previousStep()}
          title="Criando reserva de escritório privativo"
          subtitle="Todos os campos são obrigatórios"
        />
        <form className="gap-8 flex flex-col" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex-col justify-start items-start gap-8 inline-flex">
            {errorMsg && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errorMsg}</p>
              </div>
            )}
            <div className="self-stretch flex-col justify-start items-start gap-px flex">
              <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
                Pagamentos adicionais
              </div>
              <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
                Informe quantos créditos serão adicionados, ajuste a data para
                cada pagamento, e acompanhe as alterações em tempo real na
                tabela abaixo.
              </div>
            </div>
            {hasProRata && (
              <div className="flex-col justify-start items-start gap-1 self-stretch flex">
                <Alert className="bg-amber-50 rounded-lg border border-yellow-500/50 w-full flex-col justify-start items-start gap-1 inline-flex">
                  <div className="flex gap-3  items-center pl-6">
                    <div className="absolute top-4 left-4">
                      <SirenIcon
                        className="text-yellow-900"
                        width={16}
                        height={16}
                      />
                    </div>
                    <div>
                      <AlertTitle className="text-base font-medium text-yellow-900 mb-0">
                        Atenção: Pró-rata será aplicada!
                      </AlertTitle>
                      <AlertDescription className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                        Este booking possui uma data de início que não coincide
                        com o começo do mês.
                      </AlertDescription>
                      <h6 className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                        Os valores de repasse e créditos serão calculados
                        proporcionalmente aos dias restantes do mês atual.
                      </h6>
                    </div>
                  </div>
                </Alert>
              </div>
            )}
            <div className="self-stretch flex-col justify-start items-start gap-4 flex">
              <div className="self-stretch justify-start items-start gap-4 inline-flex">
                <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                  <label className="block text-sm font-medium text-gray-700">
                    Créditos adicionais
                  </label>
                  <div className="w-full">
                    <Controller
                      control={control}
                      name="upsellOption"
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          label="UpsellOption"
                          name="upsellOption"
                          type="text"
                          inputMode="decimal"
                          value={field.value || ""}
                          onChange={(event) => {
                            field.onChange({
                              ...event,
                              target: {
                                ...event.target,
                                value: JustDecimal(event.target.value),
                              },
                            });
                          }}
                          onBlur={(event) => {
                            setUpsellOption(Number(event.target.value));
                          }}
                          required
                          error={
                            fieldState.error ? fieldState.error.message : ""
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                {hasProRata && (
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <label className="block text-sm font-medium text-gray-700">
                      Créditos em pró-rata
                    </label>
                    <div className="w-full">
                      <Controller
                        control={control}
                        name="upsellProRataOption"
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label="UpsellProRataOption"
                            name="upsellProRataOption"
                            type="text"
                            inputMode="decimal"
                            value={field.value || ""}
                            onChange={(event) => {
                              field.onChange({
                                ...event,
                                target: {
                                  ...event.target,
                                  value: JustDecimal(event.target.value),
                                },
                              });
                            }}
                            onBlur={(event) => {
                              handleProRataOptionChange(
                                Number(event.target.value),
                              );
                            }}
                            required
                            error={
                              fieldState.error ? fieldState.error.message : ""
                            }
                            disable={true}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {simulatedUpsell?.newPayments && (
              <AdditionalPaymentsTable
                data={simulatedUpsell.newPayments}
                proRataCredits={upsellProRataOption}
                UpdatePayments={UpdateUpsellPayments}
              />
            )}
            <div className="flex-col justify-start items-start gap-px inline-flex">
              <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
                Ciclos afetados
              </div>
              <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
                Revise abaixo como os ciclos ao longo da reserva serão
                impactados por ela.
              </div>
            </div>
            {simulatedUpsell?.updatedCycles ? (
              <UpsellCyclesTable data={simulatedUpsell.updatedCycles} />
            ) : (
              <div className="self-stretch bg-white rounded-lg border border-zinc-200 flex-col justify-start items-start flex">
                <div className="self-stretch p-6 justify-start items-start inline-flex">
                  <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex gap-1">
                    <div className="self-stretch text-bg-black text-2xl font-semibold">
                      Ciclos Afetados
                    </div>
                    <div className="self-stretch h-5 text-bg-black text-sm font-normal leading-tight">
                      Selecione um valor de créditos adicionais para mais
                      informações.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="mt-8 flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={previousStep}
              className="px-4 py-2"
              disabled={isSubmitting}
            >
              Voltar
            </Button>
            <Button type="submit" className="px-4 py-2" disabled={isSubmitting}>
              {isSubmitting ? "Processando..." : "Avançar para revisão"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
